import {
  InputAddManualLineToPurchaseOrder,
  PurchaseItemType,
  ResultPurchaseOrder
} from '../../../api/dealer-api-interface-all';
import { getApiFactory } from '../../../api/api-injector';
import type { NullPromise } from '../../../../webmodule-common/other/ui/events';

// Why store finish as a bare code (e.g. "NF") rather than the group-bracket form?
// BomMaterialAggregator.KeyFor uses cp.FinishCode, which is V6's CodeOf value -
// a bare code like "MER", never "MER (MERANTI)". Manual lines must produce the same
// key to merge with BOM-sourced lines, so finish.code (bare code) is the correct value.
// Assumption: the picker's finish.code equals the V6 CodeOf result for the colour.

export interface AdHocLineSelection {
  purchaseOrderId: string;
  recordVersion: string;
  itemType: PurchaseItemType;
  resource: { libraryCode: string; code: string; description: string };
  finish: { libraryCode: string; code: string; description: string } | null;
  notOrder: boolean;
  quantity: number;
  barLength?: number;
  barLengthDisplay?: string;
  width?: number;
  widthDisplay?: string;
  height?: number;
  heightDisplay?: string;
  packSize?: number;
  rollLength?: number;
  rollLengthDisplay?: string;
}

// Persist a collected selection. Kept in the data layer (next to buildAdHocLineInput) so the
// picker orchestrators can run the interactive picking outside the save indicator and the caller
// wraps only this network call in it (mirrors the quote-item add flow: pick first, then save).
export async function submitAdHocLine(selection: AdHocLineSelection): NullPromise<ResultPurchaseOrder> {
  const input = buildAdHocLineInput(selection);
  return await getApiFactory().fabricationPurchaseOrder().addManualLineToPurchaseOrder(input);
}

interface V6ObjectReferenceParts {
  classCode: string;
  libraryCode: string;
  rawCode: string;
}

// Split a V6 object reference "[CLASS:LIB:CODE]" into its three segments
// (e.g. "[COL:DEMO:NF]" -> { classCode: "COL", libraryCode: "DEMO", rawCode: "NF" }).
// Missing segments come back as "", matching V6's own CodeOf/LibOf, which return "" for a
// reference they cannot parse rather than throwing.
//
// rawCode is the segment as written. That is the bare code for references V6's COM layer builds
// from ids (ObjLite serialises class:lib:code), but some V6 endpoints hand-build colour refs as
// "[COL:LIB:CODE (GROUP)]", where CodeOf yields just "CODE".
export function parseObjectReference(reference: string): V6ObjectReferenceParts {
  const segments = reference.replace(/^\[|\]$/g, '').split(':');
  return {
    classCode: segments[0] ?? '',
    libraryCode: segments[1] ?? '',
    rawCode: segments[2] ?? ''
  };
}

export function buildAdHocLineInput(s: AdHocLineSelection): InputAddManualLineToPurchaseOrder {
  return {
    purchaseOrderId: s.purchaseOrderId,
    recordVersion: s.recordVersion,
    itemType: s.itemType,
    libraryCode: s.resource.libraryCode,
    code: s.resource.code,
    finish: s.finish?.code ?? '',
    finishLibraryCode: s.finish?.libraryCode ?? '',
    resourceDescription: s.resource.description,
    finishDescription: s.finish?.description ?? '',
    quantity: s.quantity,
    notOrder: s.notOrder,
    barLength: s.barLength ?? null,
    barLengthDisplay: s.barLengthDisplay ?? null,
    width: s.width ?? null,
    widthDisplay: s.widthDisplay ?? null,
    height: s.height ?? null,
    heightDisplay: s.heightDisplay ?? null,
    packSize: s.packSize ?? null,
    rollLength: s.rollLength ?? null,
    rollLengthDisplay: s.rollLengthDisplay ?? null
  };
}
